import React, { type FC, forwardRef, type ReactNode } from "react";
import classNames from "classnames";
import CircleSpinner from "./CircleSpinner/CircleSpinner";
import { LinkButton } from "./Link";
import {
  Dialog,
  DialogActions,
  DialogContent,
  type DialogProps,
  Slide,
  styled,
  type SxProps,
  type Theme,
  type TransitionProps,
} from "~/common/mui";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useNumericScreenSize } from "../hooks/useNumericScreenSize";
import { t } from "i18next";

const ModalDialog = styled(Dialog)(({ theme }) => ({
  "& .scha__modal__bg": {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: "rgba(0, 0, 0, 0.5)",
    zIndex: 1299,
  },
  "& .scha__modal": {
    position: "absolute",
    top: "10vh",
    left: 0,
    right: 0,
    margin: "auto",
    width: "100%",
    background: "white",
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingBottom: theme.spacing(3.75),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("tablet")]: {
      width: "720px",
      maxHeight: "80vh",
    },
    [theme.breakpoints.up("desktop-s")]: {
      width: "752px",
    },
    "&--scanner": {
      maxHeight: "unset",
      height: "100dvh",
      bottom: "unset",
    },
    "&--small": {
      [theme.breakpoints.up("phone")]: {
        width: "500px",
      },
    },
    "&--large": {
      [theme.breakpoints.up("desktop-s")]: {
        width: "992px",
      },
    },
    "&--huge": {
      [theme.breakpoints.up("desktop-s")]: {
        width: "1200px",
        maxHeight: "800px",
      },
    },
    "&--has-footer": {
      paddingBottom: 0,
    },
    "&--no-padding": {
      padding: "0 !important",
    },
    "&--scrollable": {
      overflow: "hidden",
      maxHeight: "unset",
      "& .scha__modal__contents__children": {
        overflow: "auto",
        maxHeight: "80vh",
        paddingRight: theme.spacing(2),
        "&::-webkit-scrollbar": {
          width: "5px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.surface.main,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.divider,
        },
      },
      "& .scha__modal__footer": {
        paddingRight: theme.spacing(3.75),
      },
    },
  },
  "& .scha__modal__full-screen-view": {
    position: "fixed",
    top: "0 !important",
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    padding: 0,
    "& .scha__modal__header": {
      padding: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      flex: "none",
      width: "100%",
      backgroundColor: theme.palette.surfaceContainer.lowest,
      "& a": {
        padding: theme.spacing(2),
      },
    },
    "& .scha__modal__contents": {
      padding: theme.spacing(2),
      overflow: "auto",
      flex: "1 1 auto",
    },
    "& .scha__modal__footer": {
      flex: "none",
      padding: theme.spacing(2),
      margin: 0,
      backgroundColor: "transparent",
    },
    "& .scha__modal__contents__children": {
      maxHeight: "initial",
    },
  },
  "& .scha__modal__quick-entry": {
    "& .scha__modal__contents .scha__modal__contents__children .scha__bulk-preview__table__row:last-child": {
      paddingBottom: theme.spacing(2.5),
    },
    "& .scha__modal__contents .scha__modal__contents__children .info-messages": {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      "& .scha__message": {
        display: "block",
        marginBottom: theme.spacing(1.25),
        "&:last-child": {
          marginBottom: 0,
        },
      },
    },
    "& .scha__modal__contents .scha__modal__contents__children > .add-new-row-button": {
      padding: 0,
      marginBottom: theme.spacing(3.75),
    },
  },
}));

const ModalHeader = styled("div")(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  "& > span": {
    ...theme.typography.titleLarge,
  },
  "&.scha__modal__header--icon-flex-end": {
    justifyContent: "flex-end",
  },
}));

const ModalContent = styled(DialogContent)({
  overflow: "auto",
  minHeight: 0,
  "&.scha__modal__bulk-preview__content": {
    display: "flex",
    flexDirection: "column",
    "& > .scha__modal__contents__children": { overflow: "auto" },
  },
  "& .scha__modal__contents__children": {
    transition: "opacity 0.4s ease, max-height 0.4s ease-in",
    opacity: 1,
  },
  "& .scha__modal__contents__spinner": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "&.scha__modal__contents__spinner--invisible": {
      display: "none",
    },
  },
  "&.scha__modal__contents--loading .scha__modal__contents__children": {
    maxHeight: 0,
    opacity: 0,
  },
});

const ModalActions = styled(DialogActions)(({ theme }) => ({
  "&.scha__modal__footer": {
    marginLeft: theme.spacing(-2.5),
    marginRight: theme.spacing(-2.5),
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.surface.main,
    ".scha__modal--no-padding &": {
      margin: 0,
    },
    "&.scha__modal__footer--border": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  "&.scha__modal__footer--border": {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const ModalTitleRoot = styled("div")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  color: theme.palette.text.primary,
}));

const ModalFooterRoot = styled("div")(({ theme }) => ({
  marginLeft: theme.spacing(-2.5),
  marginRight: theme.spacing(-2.5),
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.surface.main,
  ".scha__modal--no-padding &": {
    margin: 0,
  },
  "&.scha__modal__footer--border": {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const ModalCloseButton = styled(LinkButton)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.container,
  borderRadius: "50%",
  alignSelf: "flex-start",
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // This component is deprecated anyway
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface ModalProps {
  children?: ReactNode;
  title?: string;
  onClose: (e: React.MouseEvent) => void;
  size?: ModalSize;
  id?: string;
  modalClassName?: string;
  modalBgClassName?: string;
  modalContentClassName?: string;
  footerChildren?: ReactNode;
  isLoading?: boolean;
  noPadding?: boolean;
  hideClose?: boolean;
  footerClassName?: string;
  headerOverride?: ReactNode;
  rootProps?: JSX.IntrinsicElements["div"] & Partial<Record<GAAttributes, string>>;
  modalPaperSx?: SxProps<Theme>;
}

type GAAttributes = "clta-modal-parent-marker" | "clta-product-name" | "clta-product-id" | "clta-total-value";

/**
 * @deprecated use Mui's Dialog instead
 */
const Modal = forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const {
    title,
    modalClassName,
    modalBgClassName,
    modalContentClassName,
    size = ModalSize.MEDIUM,
    onClose,
    isLoading,
    noPadding,
    footerChildren,
    id,
    hideClose,
    footerClassName,
    headerOverride,
    rootProps,
    modalPaperSx,
    children,
  } = props;

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const modalClasses = classNames(modalClassName, modalBgClassName);

  const contentCls = classNames("scha__modal__contents", {
    "scha__modal--no-padding": noPadding,
    "scha__modal__contents--loading": isLoading,
  });
  const headerCls = classNames("scha__modal__header", {
    "scha__modal__header--icon-flex-end": !title,
  });

  const spinnerCls = classNames("scha__modal__contents__spinner", {
    "scha__modal__contents__spinner--invisible": !isLoading,
  });

  const handleClose: DialogProps["onClose"] = (e: React.MouseEvent<HTMLAnchorElement>, reason) => {
    e.preventDefault();
    e.stopPropagation();
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return; // Prevent closing on outside click or escape key
    }
    onClose(e);
  };

  const getCustomWidth = () => {
    switch (size) {
      case ModalSize.LARGE:
        return "1200px";
      case ModalSize.MEDIUM:
        return "980px";
      case ModalSize.SMALL:
        return "780px";
      case ModalSize.TABLET:
        return "720px";
    }
  };

  return (
    <ModalDialog
      ref={ref}
      open={true}
      onClose={handleClose}
      slots={{
        transition: isMobile ? Transition : undefined,
      }}
      fullScreen={isMobile}
      slotProps={{
        paper: {
          className: modalClasses,
          sx: [
            (theme) => ({
              maxWidth: getCustomWidth(),
              [theme.breakpoints.down("tablet")]: {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                maxHeight: "100vh",
                position: "fixed",
                margin: 0,
                bottom: 0,
                left: 0,
                right: 0,
              },
            }),
            ...(Array.isArray(modalPaperSx) ? modalPaperSx : modalPaperSx ? [modalPaperSx] : []),
          ],
        },
      }}
    >
      {headerOverride ??
        (!(!title && hideClose) && (
          <ModalHeader className={classNames(headerCls)}>
            {title && <ModalTitle>{title}</ModalTitle>}
            {!hideClose && (
              <ModalCloseButton
                onClick={onClose}
                aria-label={t("close")}
                id={id}
              >
                <CloseRoundedIcon fontSize="medium" color="secondary" />
              </ModalCloseButton>
            )}
          </ModalHeader>
        ))}

      <ModalContent
        className={classNames(contentCls, modalContentClassName)}
        sx={{
          paddingTop: !title && hideClose ? "30px" : "0px",
        }}
      >
        <div className={spinnerCls}>
          <CircleSpinner />
        </div>
        <div className="scha__modal__contents__children">{!isLoading && children}</div>
      </ModalContent>
      {footerChildren && <ModalActions className={classNames(footerClassName)}>{footerChildren}</ModalActions>}
    </ModalDialog>
  );
});

export enum ModalSize {
  LARGE,
  MEDIUM,
  SMALL,
  TABLET,
}

export default Modal;

export const ModalTitle: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <ModalTitleRoot className={classNames("scha__modal__title", className)} {...rest} />
);

export const ModalFooter: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <ModalFooterRoot className={classNames("scha__modal__footer", className)} {...rest} />
);
